import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import ReactPlayer from "react-player";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/react";
import palette from "assets/theme/base/palette";

const ContentPlayerDialog = ({
  playContent,
  closeContentPlayer,
  content,
  userMetricsData,
  setUserMetricData,
  setViewedContent,
  userId,
  gamePlan,
  setGamePlan,
  previousGamePlan,
  setPreviousGamePlan,
  previousYearsGamePlan,
  setPreviousYearsGamePlan,
  setHasCompletedAll,
  week,
  planType,
  yearIndex,
  t,
}) => {
  const [showCongrats, setShowCongrats] = useState(false);
  const { content_url: src, content_type, open_player, title } = content;

  useEffect(() => {
    if (open_player) {
      updateMetrics();
    }
  }, []);

  const updateMetrics = () => {
    // --- Game Plan Logic ---
    if (planType && (gamePlan || previousGamePlan)) {
      let update = true;
      if (planType === "myGamePlan" && gamePlan) {
        const watched = gamePlan[0].watched_content;
        if (!watched.includes(title)) watched.push(title);
        else update = false;
      } else if (planType === "previousGamePlan" && previousGamePlan) {
        const watched = previousGamePlan[week].watched_content;
        if (!watched.includes(title)) watched.push(title);
        else update = false;
      } else if (planType === "previousYearsGamePlan" && previousYearsGamePlan) {
        const watched = previousYearsGamePlan[yearIndex - 1][week].watched_content;
        if (!watched.includes(title)) watched.push(title);
        else update = false;
      }

      if (update) {
        call(`users/${userId}/gameplan`, "PUT", {
          body: {
            game_plan: gamePlan,
            previous_game_plan: previousGamePlan,
            previous_years_game_plan: previousYearsGamePlan,
          },
        })
          .then((res) => {
            if (res.status === 200) {
              if (planType === "myGamePlan" && gamePlan) {
                const watchedLength = gamePlan[0].watched_content.length;
                const totalLength = gamePlan[0].content.length;
                if (watchedLength === totalLength) {
                  setShowCongrats(true);
                  setHasCompletedAll?.(true);
                }
              }
              setGamePlan?.([...gamePlan]);
              setPreviousGamePlan?.([...previousGamePlan]);
            }
          })
          .catch((err) => {
            console.log(err);
            Sentry.captureException(err);
          });
      }
    }

    let viewedContent = userMetricsData.viewedcontent || [];
    let weeklyContent = userMetricsData.weeklycontent || [];
    if (!weeklyContent.includes(title)) weeklyContent = [...weeklyContent, title];
    userMetricsData["weeklycontent"] = weeklyContent;
    if (!viewedContent.includes(title)) viewedContent = [...viewedContent, title];
    userMetricsData["viewedcontent"] = viewedContent;
    userMetricsData["eventkey"] = { event: "viewcontent", value: title };

    call(`users/${userId}/contentmetrics`, "PUT", { body: userMetricsData })
      .then((res) => {
        if (res.status === 200) {
          setUserMetricData(userMetricsData);
          setViewedContent?.([...viewedContent]);
        }
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
      });
  };

  const PlayerContent = () => (
    <>
      {open_player ? (
        <ReactPlayer
          url={src}
          playing={false}
          controls
          width="100%"
          config={{
            youtube: {
              playerVars: {
                rel: 0,
                modestbranding: 1,
                showinfo: 0,
                iv_load_policy: 3,
                fs: 0,
                playsinline: 1,
              },
            },
          }}
        />
      ) : (
        <MDBox display="flex" justifyContent="center" alignItems="center">
          <MDButton
            onClick={updateMetrics}
            component="a"
            href={src}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            size="large"
            color="info"
          >
            {t("playContent.openInNewTab")}
          </MDButton>
        </MDBox>
      )}
    </>
  );

  if (playContent && closeContentPlayer) {
    return (
      <Dialog
        open={playContent}
        onClose={() => closeContentPlayer(showCongrats)}
        fullWidth
        maxWidth={open_player ? "md" : "xs"}
        PaperProps={{
          sx: { borderRadius: "24px", overflow: "hidden" },
        }}
      >
        {/* Close button */}
        <MDBox
          onClick={() => closeContentPlayer(showCongrats)}
          sx={{
            position: "absolute", top: 12, right: 12, zIndex: 10,
            width: 36, height: 36, borderRadius: "50%",
            bgcolor: palette.neutral[100],
            border: `1px solid ${palette.neutral[200]}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            transition: "all 180ms ease",
            "&:hover": { bgcolor: palette.neutral[200] },
          }}
        >
          <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
        </MDBox>

        {open_player ? (
          <DialogContent sx={{ p: 0 }}>
            <PlayerContent />
          </DialogContent>
        ) : (
          <MDBox sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
            {/* Decorative icon */}
            <MDBox sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
              {/* Sparkles */}
              <MDBox sx={{ position: "absolute", top: -10, left: -18, color: palette.brand[400], fontSize: "18px", lineHeight: 1 }}>✦</MDBox>
              <MDBox sx={{ position: "absolute", bottom: 0, left: -12, color: "#f48326", fontSize: "10px", lineHeight: 1 }}>●</MDBox>
              <MDBox sx={{ position: "absolute", top: 2, right: -20, color: "#9c27b0", fontSize: "10px", lineHeight: 1 }}>●</MDBox>
              <MDBox sx={{ position: "absolute", bottom: -8, right: -16, color: palette.brand[400], fontSize: "14px", lineHeight: 1 }}>✦</MDBox>

              {/* Icon circle */}
              <MDBox sx={{
                width: 100, height: 100, borderRadius: "50%",
                bgcolor: palette.brand[50],
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon sx={{ fontSize: "48px !important", color: palette.brand[500] }}>open_in_new</Icon>
              </MDBox>
            </MDBox>

            {/* Title */}
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "text.primary", mb: 1 }}>
              {title}
            </MDTypography>

            {/* Divider */}
            <Divider sx={{ width: 40, mx: "auto", mb: 2, borderBottomWidth: "2px", borderColor: palette.neutral[300] }} />

            {/* Description */}
            <MDTypography variant="body2" sx={{ color: "text.secondary", mb: 3, lineHeight: 1.7 }}>
              {t("playContent.openInNewTabDescription") || "You can open this content in a new tab to view it without leaving the current page."}
            </MDTypography>

            {/* Open in new tab button */}
            <MDButton
              onClick={updateMetrics}
              component="a"
              href={src}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ borderRadius: "12px", py: 1.5, mb: 1.5, fontSize: "0.85rem", letterSpacing: "0.06em" }}
            >
              <Icon sx={{ mr: 1, fontSize: "18px !important" }}>open_in_new</Icon>
              {t("playContent.openInNewTab")}
            </MDButton>

            {/* Cancel button */}
            <MDButton
              onClick={() => closeContentPlayer(showCongrats)}
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: "12px", py: 1.5, fontSize: "0.85rem",
                letterSpacing: "0.06em", borderColor: palette.neutral[300],
                color: palette.neutral[600],
                "&:hover": { bgcolor: palette.neutral[100], borderColor: palette.neutral[400] },
              }}
            >
              {t("playContent.cancel") || "Cancel"}
            </MDButton>
          </MDBox>
        )}
      </Dialog>
    );
  }

  return (
    <MDBox alignItems="center" color="white" margin={5}>
      <PlayerContent />
    </MDBox>
  );
};

ContentPlayerDialog.defaultProps = {
  playContent: false,
  closeContentPlayer: null,
  content: { content_url: "", content_type: "video", open_player: true, title: "" },
  userMetricsData: { viewedcontent: [] },
  setUserMetricData: () => {},
  setViewedContent: () => {},
  userId: "",
  gamePlan: null,
  setGamePlan: () => {},
  previousGamePlan: null,
  setPreviousGamePlan: () => {},

  previousYearsGamePlan: null,
  setPreviousYearsGamePlan: () => {},

  setHasCompletedAll: () => {},
  week: 0,
  planType: null,
  yearIndex: 0,
  t: (key) => key,
};

ContentPlayerDialog.propTypes = {
  playContent: PropTypes.bool,
  closeContentPlayer: PropTypes.func,
  content: PropTypes.shape({
    content_url: PropTypes.string,
    content_type: PropTypes.string,
    open_player: PropTypes.bool,
    title: PropTypes.string,
  }),
  userMetricsData: PropTypes.shape({
    viewedcontent: PropTypes.arrayOf(PropTypes.string),
  }),
  setUserMetricData: PropTypes.func,
  setViewedContent: PropTypes.func,
  userId: PropTypes.string,
  gamePlan: PropTypes.array,
  setGamePlan: PropTypes.func,
  previousGamePlan: PropTypes.array,
  setPreviousGamePlan: PropTypes.func,
  previousYearsGamePlan: PropTypes.array,
  setPreviousYearsGamePlan: PropTypes.func,
  setHasCompletedAll: PropTypes.func,
  week: PropTypes.number,
  planType: PropTypes.string,
  yearIndex: PropTypes.number,
  t: PropTypes.func,
};

export default ContentPlayerDialog;
